import { EVENTS } from '@archetype-themes/utils/events'

/**
 * VariantSelectModal
 * Handles opening a compact modal for variant selection when adding products to cart.
 * Used in search results and cart recommendations.
 */
class VariantSelectModal {
  constructor() {
    this.modal = null
    this.currentProduct = null
    this.currentVariantId = null
    this.abortController = null
    
    this.init()
  }

  init() {
    // Find or create tool-tip element
    this.modal = document.querySelector('tool-tip[data-tool-tip="VariantSelect"]')
    
    if (!this.modal) {
      // Create modal if it doesn't exist
      this.modal = document.createElement('tool-tip')
      this.modal.setAttribute('data-tool-tip', 'VariantSelect')
      document.body.appendChild(this.modal)
    }

    // Listen for button clicks that should open the modal
    document.addEventListener('click', this.handleButtonClick.bind(this))
    
    // Listen for variant changes in the modal
    document.addEventListener('click', this.handleVariantChange.bind(this))
    
    // Listen for add to cart button clicks in the modal
    document.addEventListener('click', this.handleAddToCart.bind(this))
  }

  /**
   * Handle clicks on add-to-cart buttons that should open the modal
   */
  handleButtonClick(e) {
    const button = e.target.closest('[data-variant-modal-trigger]')
    if (!button) return

    const productHandle = button.getAttribute('data-product-handle')
    const hasMultipleVariants = button.getAttribute('data-has-multiple-variants') === 'true'
    
    // Only open modal for products with multiple variants
    if (!hasMultipleVariants || !productHandle) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    
    this.open(productHandle, button)
  }

  /**
   * Open the variant selection modal
   */
  async open(productHandle, triggerButton) {
    if (!productHandle) return

    try {
      // Show loading state
      this.showLoading()

      // Fetch product data
      const response = await fetch(`${window.Shopify.routes.root}products/${productHandle}.js`)
      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`)
      }

      const product = await response.json()
      this.currentProduct = product
      
      // Check if product actually has multiple available variants
      const availableVariants = product.variants.filter(v => v.available)
      if (availableVariants.length <= 1) {
        // Only one variant or none - don't show modal, add directly to cart
        console.log('[VariantSelectModal] Product has only one variant, adding directly to cart')
        const singleVariant = availableVariants[0] || product.variants[0]
        if (singleVariant && triggerButton) {
          // Update button with variant ID and trigger direct add to cart
          triggerButton.setAttribute('data-variant-id', singleVariant.id)
          triggerButton.removeAttribute('data-variant-modal-trigger')
          // Trigger click again to add to cart directly
          triggerButton.click()
          return
        }
        return
      }
      
      // Render modal content
      this.renderModal(product)
      
      // Open the tool-tip
      this.openToolTip()
    } catch (error) {
      console.error('Error opening variant modal:', error)
      alert('Er is een fout opgetreden bij het laden van het product.')
    }
  }

  /**
   * Show loading state in modal
   */
  showLoading() {
    const content = this.modal.querySelector('[data-tool-tip-content]')
    if (content) {
      content.innerHTML = `
        <div class="variant-modal__loading">
          <div class="spinner"></div>
        </div>
      `
    }
  }

  /**
   * Render modal content with product data
   */
  renderModal(product) {
    const content = this.modal.querySelector('[data-tool-tip-content]')
    if (!content) return

    const selectedVariant = product.variants.find(v => v.available) || product.variants[0]
    this.currentVariantId = selectedVariant?.id

    // Build variant buttons - exact same structure as product page (variant-button.liquid)
    const availableVariants = product.variants.filter(v => v.available)
    const variantButtonsHTML = availableVariants.map((variant) => {
      const variantOptions = variant.options || [
        variant.option1,
        variant.option2,
        variant.option3
      ].filter((option) => option && option.trim() !== '')

      const optionText = variantOptions.join(' / ')
      const isSelected = variant.id === selectedVariant?.id

      return `
        <button
          type="button"
          class="variant__button-label${isSelected ? ' is-selected' : ''}"
          value="${variant.id}"
          data-price-cents="${variant.price}"
          data-compare-at-cents="${variant.compare_at_price || 0}"
          data-variant-button
          aria-pressed="${isSelected ? 'true' : 'false'}"
        >
          ${optionText}
        </button>
      `
    }).join('')

    // Calculate price display (same styling as product page)
    const onSale = selectedVariant?.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price
    const priceHTML = `
      <span class="grid-product__price--current" data-price-current>${this.formatMoney(selectedVariant.price)}</span>
      ${onSale ? `<span class="grid-product__price--original" data-price-compare>${this.formatMoney(selectedVariant.compare_at_price)}</span>` : ''}
    `

    // Get product image
    const imageUrl = selectedVariant?.featured_image || product.featured_image || ''
    const imageSrc = imageUrl ? this.getImageUrl(imageUrl, 300) : ''

    // Render modal HTML
    content.innerHTML = `
      <div class="variant-modal__content">
        ${imageSrc ? `
          <div class="variant-modal__image-wrapper">
            <img src="${imageSrc}" alt="${product.title}" class="variant-modal__image" data-variant-image>
          </div>
        ` : ''}
        <div class="variant-modal__info">
          <h3 class="variant-modal__title">${product.title}</h3>
          <div class="jdgm-widget jdgm-preview-badge" data-id="${product.id}"></div>
          <div class="grid-product__price variant-modal__price" data-price-container>
            ${priceHTML}
          </div>
          <fieldset class="variant-button-wrap">
            <legend class="variant__label">Variant</legend>
            ${variantButtonsHTML}
          </fieldset>
          <button type="button" class="btn variant-modal__add-btn" data-variant-add-btn>
            <span>${(window.theme && window.theme.strings && window.theme.strings.addToCartShort) || 'Add'}</span>
          </button>
        </div>
      </div>
    `

    // Initialize Judge.me reviews (same as product page)
    this.initJudgeMeReviews()
  }

  /**
   * Initialize Judge.me review badges
   */
  initJudgeMeReviews() {
    if (typeof jdgm !== 'undefined' && typeof jdgm.customizeBadges === 'function') {
      setTimeout(() => jdgm.customizeBadges(), 100)
    }
  }

  /**
   * Handle variant selector change (radio buttons)
   */
  handleVariantChange(e) {
    const variantButton = e.target.closest('[data-variant-button]')

    if (!variantButton || !this.modal.contains(variantButton)) {
      return
    }

    e.preventDefault()
    e.stopPropagation()

    const variantId = variantButton.value
    this.currentVariantId = variantId

    // Update selected styling and accessibility state
    this.modal.querySelectorAll('[data-variant-button]').forEach((button) => {
      const isSelected = button === variantButton

      button.classList.toggle('is-selected', isSelected)
      button.setAttribute('aria-pressed', String(isSelected))
    })

    // Update price
    const priceCents = Number(variantButton.dataset.priceCents)
    const compareAtCents = Number(variantButton.dataset.compareAtCents)

    const priceContainer = this.modal.querySelector('[data-price-container]')

    if (priceContainer) {
      const onSale =
        Number.isFinite(compareAtCents) &&
        compareAtCents > priceCents

      priceContainer.innerHTML = `
        <span class="grid-product__price--current" data-price-current>
          ${this.formatMoney(priceCents)}
        </span>
        ${
          onSale
            ? `<span class="grid-product__price--original" data-price-compare>
                ${this.formatMoney(compareAtCents)}
              </span>`
            : ''
        }
      `
    }

    // Update product image
    const variant = this.currentProduct?.variants.find(
      (item) => String(item.id) === String(variantId)
    )

    if (variant?.featured_image) {
      const imageElement = this.modal.querySelector('[data-variant-image]')

      if (imageElement) {
        imageElement.src = this.getImageUrl(variant.featured_image, 300)
      }
    }
  }

  /**
   * Handle add to cart button click
   */
  async handleAddToCart(e) {
    const button = e.target.closest('[data-variant-add-btn]')
    if (!button || !this.modal.contains(button)) return

    e.preventDefault()
    e.stopPropagation()

    if (!this.currentVariantId) {
      const strings = (window.theme && window.theme.strings) || {}
      alert(strings.cartErrorSelectVariant || 'Please select a variant first')
      return
    }

    if (button.classList.contains('btn--loading')) return

    button.classList.add('btn--loading')
    const buttonText = button.querySelector('span')
    const originalText =
      buttonText?.textContent ||
      (window.theme && window.theme.strings && window.theme.strings.addToCartShort) ||
      'Voeg toe'

    try {
      const data = {
        items: [
          {
            id: this.currentVariantId,
            quantity: 1
          }
        ],
        sections: ['cart-ajax'],
        sections_url: `${window.Shopify.routes.root}cart`
      }

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: JSON.stringify(data),
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const payload = await response.json()

      if (payload?.status === 422 || payload?.status === 'bad_request') {
        if (payload.description) {
          alert(payload.description)
        }
        return
      }

      // Dispatch event for cart updates
      document.dispatchEvent(
        new CustomEvent(EVENTS.ajaxProductAdded, {
          bubbles: true,
          detail: {
            product: payload,
            addToCartBtn: button
          }
        })
      )

      // Close modal
      this.close()
    } catch (error) {
      console.error('Error adding to cart:', error)
      alert('Er is een fout opgetreden bij het toevoegen aan de winkelwagen.')
    } finally {
      button.classList.remove('btn--loading')
      if (buttonText) buttonText.textContent = originalText
    }
  }

  /**
   * Open the tool-tip modal
   */
  openToolTip() {
    const event = new CustomEvent('tooltip:open', {
      detail: {
        context: 'VariantSelect',
        content: this.modal.querySelector('[data-tool-tip-content]')?.innerHTML || ''
      },
      bubbles: true
    })
    document.dispatchEvent(event)
  }

  /**
   * Close the modal
   */
  close() {
    const event = new CustomEvent('tooltip:close', {
      detail: {
        context: 'VariantSelect'
      },
      bubbles: true
    })
    this.modal.dispatchEvent(event)
  }

  /**
   * Format money value
   */
  formatMoney(cents) {
    if (typeof cents !== 'number') return ''
    const formatter = new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    })
    return formatter.format(cents / 100)
  }

  /**
   * Get image URL with size parameter
   */
  getImageUrl(imageUrl, width) {
    if (!imageUrl) return ''
    
    // Handle image objects (Shopify image objects have src property)
    let url = ''
    if (typeof imageUrl === 'string') {
      url = imageUrl
    } else if (imageUrl && typeof imageUrl === 'object') {
      // Could be a Shopify image object with src, url, or other properties
      url = imageUrl.src || imageUrl.url || imageUrl.originalSrc || ''
      // If still not a string, try to stringify or return empty
      if (typeof url !== 'string') {
        console.warn('[VariantSelectModal] Image URL is not a string:', imageUrl)
        return ''
      }
    } else {
      console.warn('[VariantSelectModal] Invalid image URL type:', typeof imageUrl, imageUrl)
      return ''
    }
    
    // If already has size parameter, replace it, otherwise add it
    if (url.includes('_')) {
      return url.replace(/_\d+x\d+/, `_${width}x`)
    }
    return url.includes('?') 
      ? `${url}&width=${width}`
      : `${url}?width=${width}`
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.variantSelectModal = new VariantSelectModal()
  })
} else {
  window.variantSelectModal = new VariantSelectModal()
}
